import { DayAndPeriod } from '../../graphql/generated/graphql.ts';
import { FC, useEffect, useState } from 'react';
import { getDay } from 'date-fns';
import { Day, Period } from '../../pages/activity-planning/steps/time-selection.tsx';
import { Box, Checkbox, Collapse, FormControlLabel, FormGroup, Paper, Stack } from '@mui/material';

export interface TimeSelectionProps {
  defaultSchedule: DayAndPeriod[];
  disabled?: boolean;
  onChange?: (schedule: DayAndPeriod[]) => void;
}

const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const TimeSelection: FC<TimeSelectionProps> = ({ defaultSchedule, onChange, disabled }) => {
  const [schedule, setSchedule] = useState<DayAndPeriod[]>(defaultSchedule);
  const [selectedDays, setSelectedDays] = useState<{ [key: string]: boolean }>(() => {
    const days: { [key: string]: boolean } = { Monday: false, Tuesday: false, Wednesday: false, Thursday: false, Friday: false, Saturday: false, Sunday: false };
    if (defaultSchedule) {
      for (const item of defaultSchedule) {
        days[item.day] = true;
      }
    }
    return days;
  });

  const [selectedTimes, setSelectedTimes] = useState<{ [key: string]: { Morning: boolean; Afternoon: boolean; Evening: boolean } }>(() => {
    const times: { [key: string]: { Morning: boolean; Afternoon: boolean; Evening: boolean } } = {
      Monday: { Morning: false, Afternoon: false, Evening: false },
      Tuesday: { Morning: false, Afternoon: false, Evening: false },
      Wednesday: { Morning: false, Afternoon: false, Evening: false },
      Thursday: { Morning: false, Afternoon: false, Evening: false },
      Friday: { Morning: false, Afternoon: false, Evening: false },
      Saturday: { Morning: false, Afternoon: false, Evening: false },
      Sunday: { Morning: false, Afternoon: false, Evening: false }
    };
    if (defaultSchedule) {
      for (const item of defaultSchedule) {
        for (const period of item.period) {
          if (period in times[item.day]) {
            times[item.day][period as keyof (typeof times)[string]] = true;
          }
        }
      }
    }
    return times;
  });

  useEffect(() => {
    if (onChange) {
      onChange(schedule);
    }
  }, [schedule]);

  const today = getDay(new Date());

  const handleDayChange = (day: string) => {
    setSelectedDays((prev) => ({
      ...prev,
      [day]: !prev[day]
    }));
  };

  const handleTimeChange = (day: string, time: keyof (typeof selectedTimes)[typeof day]) => {
    setSelectedTimes((prev) => ({
      ...prev,
      [day]: { ...prev[day], [time]: !prev[day][time] }
    }));
  };

  const handleDaySelection = async (selectedDay: Day) => {
    setSchedule((prevSchedule: any) => {
      const newSchedule = [...prevSchedule];
      const day = newSchedule.find((item) => item.day === selectedDay);
      if (day) {
        return newSchedule.filter((item) => item.day !== selectedDay);
      } else {
        return [...newSchedule, { day: selectedDay, period: [] }];
      }
    });
  };

  const handlePeriodSelection = (selectedDay: Day, selectedPeriod: Period) => {
    setSchedule((prevSchedule) => {
      const newSchedule = prevSchedule.map((item) => ({ ...item, period: [...item.period] }));
      const day = newSchedule.find((item) => item.day === selectedDay);
      if (day) {
        if (day.period.includes(selectedPeriod)) {
          day.period = day.period.filter((period) => period !== selectedPeriod);
          if (day.period.length === 0) {
            return newSchedule.filter((item) => item.day !== selectedDay);
          }
        } else {
          day.period = [...day.period, selectedPeriod];
        }
      } else {
        newSchedule.push({ day: selectedDay, period: [selectedPeriod] } as any);
      }
      return newSchedule;
    });
  };

  return (
    <Stack spacing={2}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflowY: 'auto'
        }}
      >
        {weekDays.map((day, index) => (
          <Stack key={day} spacing={1}>
            <FormControlLabel
              control={
                <Checkbox
                  disabled={index + 1 < today || disabled || selectedTimes[day].Morning || selectedTimes[day].Afternoon || selectedTimes[day].Evening}
                  checked={selectedDays[day]}
                  onChange={() => {
                    handleDaySelection(day as Day);
                    handleDayChange(day);
                  }}
                />
              }
              label={day}
            />
            <Collapse in={selectedDays[day]}>
              <Stack spacing={1} sx={{ pl: 3 }}>
                <Paper sx={{ p: 2 }} variant="outlined">
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          disabled={disabled}
                          checked={selectedTimes[day].Morning}
                          onChange={() => {
                            handlePeriodSelection(day as Day, 'Morning');
                            handleTimeChange(day, 'Morning');
                          }}
                        />
                      }
                      label="Morning"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          disabled={disabled}
                          checked={selectedTimes[day].Afternoon}
                          onChange={() => {
                            handlePeriodSelection(day as Day, 'Afternoon');
                            handleTimeChange(day, 'Afternoon');
                          }}
                        />
                      }
                      label="Afternoon"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          disabled={disabled}
                          checked={selectedTimes[day].Evening}
                          onChange={() => {
                            handlePeriodSelection(day as Day, 'Evening');
                            handleTimeChange(day, 'Evening');
                          }}
                        />
                      }
                      label="Evening"
                    />
                  </FormGroup>
                </Paper>
              </Stack>
            </Collapse>
          </Stack>
        ))}
      </Box>
    </Stack>
  );
};
